<template>
    <div class="restore-container">
        <div class="restore-card">
            <h2>데이터 복원 (Restore System)</h2>
            <p class="desc">
                서버가 초기화 상태이거나 데이터를 과거 상태로 되돌려야 할 때 사용합니다.<br>
                백업 파일(<code>.tar.gz</code>)을 업로드하여 DB와 사진 데이터 전체를 복원할 수 있습니다.
            </p>
            
            <div class="upload-zone" :class="{ 'is-restoring': isRestoring }">
                <input 
                    type="file" 
                    ref="restoreFileInput" 
                    accept=".tar.gz,.gz" 
                    style="display:none" 
                    @change="handleFileSelected" 
                />
                <button 
                    type="button" 
                    class="select-btn" 
                    @click="triggerFileSelect" 
                    :disabled="isRestoring"
                >
                    {{ isRestoring ? '복원 진행 중...' : '백업 파일 선택 및 복원 시작' }}
                </button>
            </div>

            <!-- Status & Progress -->
            <div v-if="isRestoring" class="status-container">
                <div class="spinner"></div>
                <p class="status-text">서버에 백업 파일 전송 및 복원 작업을 수행하고 있습니다.</p>
                <p class="warning-text">⚠️ 복원이 진행되는 동안 브라우저 창을 닫지 마세요. (용량에 따라 수 분 소요)</p>
            </div>

            <div class="back-link">
                <router-link :to="{ name: 'admin' }">← 로그인 페이지로 돌아가기</router-link>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator'
import { adminApi } from '@/store'

@Component
export default class Restore extends Vue {
    isRestoring = false
    restorePolling = null

    triggerFileSelect() {
        this.$refs.restoreFileInput.click();
    }

    async handleFileSelected(event) {
        const file = event.target.files[0];
        if (!file) return;

        const sizeInGB = (file.size / 1024 / 1024 / 1024).toFixed(2);
        const confirmMsg = `"${file.name}" (${sizeInGB} GB) 백업 파일로 서버를 복원하시겠습니까?\n\n⚠️ 주의: 현재 로컬 데이터베이스와 모든 업로드된 이미지 파일이 완전히 삭제되고 덮어씌워집니다.`;
        
        if (!confirm(confirmMsg)) {
            this.$refs.restoreFileInput.value = '';
            return;
        }

        this.isRestoring = true;
        try {
            const formData = new FormData();
            formData.append('file', file);

            // POST /backup/restore
            await adminApi.post('/backup/restore', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 0, // Disable timeout for large file uploads
            });

            alert('복원 프로세스가 백그라운드에서 시작되었습니다.');
            this.startRestorePolling();
        } catch (e) {
            alert('복원 시작 실패: ' + e.message);
            this.isRestoring = false;
        }
        this.$refs.restoreFileInput.value = '';
    }

    startRestorePolling() {
        this.restorePolling = setInterval(async () => {
            try {
                const res = await adminApi.get('/backup/status');
                if (!res.data.isRestoring) {
                    clearInterval(this.restorePolling);
                    this.isRestoring = false;

                    if (res.data.lastRestoreResult && res.data.lastRestoreResult.success) {
                        alert('복원이 성공적으로 완료되었습니다!\n\n' + res.data.lastRestoreResult.message + '\n\n로컬 서버를 재시작해 주세요.');
                        this.$router.push({ name: 'admin' }).catch(() => {});
                    } else {
                        const errMsg = res.data.lastRestoreResult ? res.data.lastRestoreResult.message : '알 수 없는 오류가 발생했습니다.';
                        alert('복원 작업 중 오류가 발생했습니다:\n\n' + errMsg);
                    }
                }
            } catch (e) {
                console.error('복원 상태 확인 오류:', e);
            }
        }, 5000);
    }

    beforeDestroy() {
        if (this.restorePolling) {
            clearInterval(this.restorePolling);
        }
    }
}
</script>

<style scoped lang="scss">
@import 'src/assets/style/common';
@import 'src/assets/style/media-query';

.restore-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - 120px);
    padding: 20px;
}

.restore-card {
    background: #ffffff;
    border: 1px solid $transparent-dark-theme;
    border-radius: 8px;
    padding: 30px;
    max-width: 500px;
    width: 100%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    text-align: center;

    h2 {
        margin-top: 0;
        color: $dark-theme;
        font-size: 24px;
        margin-bottom: 15px;
    }

    .desc {
        color: #666;
        font-size: 14px;
        line-height: 1.6;
        margin-bottom: 25px;
    }

    .upload-zone {
        border: 2px dashed $transparent-dark-theme;
        border-radius: 6px;
        padding: 40px 20px;
        background: #fafafa;
        margin-bottom: 20px;
        transition: all 0.3s;

        &.is-restoring {
            border-color: #ffc107;
            background: #fffdf5;
        }

        .select-btn {
            background: #28a745;
            color: white;
            border: none;
            padding: 12px 24px;
            font-size: 15px;
            font-weight: bold;
            border-radius: 4px;
            cursor: pointer;
            transition: background 0.2s;

            &:hover:not(:disabled) {
                background: #218838;
            }

            &:disabled {
                background: #6c757d;
                cursor: not-allowed;
            }
        }
    }

    .status-container {
        margin: 20px 0;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 4px;
        border-left: 4px solid #ffc107;

        .spinner {
            display: inline-block;
            width: 30px;
            height: 30px;
            border: 3px solid rgba(0, 0, 0, 0.1);
            border-radius: 50%;
            border-top-color: #ffc107;
            animation: spin 1s ease-in-out infinite;
            margin-bottom: 10px;
        }

        .status-text {
            font-weight: bold;
            color: #333;
            margin: 5px 0;
        }

        .warning-text {
            font-size: 12px;
            color: #dc3545;
            margin: 5px 0 0 0;
        }
    }

    .back-link {
        margin-top: 25px;
        font-size: 14px;
        a {
            color: #007bff;
            text-decoration: none;
            &:hover {
                text-decoration: underline;
            }
        }
    }
}

@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>
